import React, {useEffect, useState} from "react";
import {
    BillAlertDto,
    BillAlertRestControllerApiFactory,
    TaxAlertDto,
    TaxCalendarRestControllerApiFactory,
} from "arteco-api-client-ts";
import {useApiManager} from "../../../../components/utils/api";
import {
    Box,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useMediaQuery,
} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import {EventAvailable, ReceiptLong} from "@mui/icons-material";
import {useNavigate} from "react-router-dom";
import {fmtEur} from "../../../../components/utils/numbers";
import {dateToString} from "../../../../components/utils/dates";
import {periodLabel} from "../tax_submission/labels";
import {
    EmptyState,
    ListCard,
    Page,
    PageHeader,
    Panel,
    SkeletonRows,
    SoftChip,
    tableContainerSx,
    tableSx,
} from "../../../../components/kit";

const taxStatusChip = (status?: string) =>
    <SoftChip label={status === "VENCIDA" ? "Vencida" : status === "PROXIMA" ? "Próxima" : "Presentada"}
              tone={status === "VENCIDA" ? "error" : status === "PROXIMA" ? "warning" : "success"}/>;

const taxPeriod = (t: TaxAlertDto) => t.period === "ANUAL" ? `Anual ${t.coveredYear}` : `${periodLabel(t.period)} ${t.coveredYear}`;

// Lo presenta la gestoría: el aviso sirve para vigilar, no para ponerse a hacerlo.
const taxFiler = (t: TaxAlertDto) => t.filer === "QUOTA"
    ? <SoftChip label="Quota" tone="neutral"/>
    : <Typography variant="body2" color="text.secondary" component="span">Arteco</Typography>;

const TaxDays = ({t}: { t: TaxAlertDto }) => t.status === "PRESENTADA"
    ? <Typography variant="body2" color="text.secondary" component="span">—</Typography>
    : <Box component="span" sx={{
        fontWeight: 700, fontVariantNumeric: "tabular-nums",
        color: (t.daysOverdue ?? 0) > 0 ? "error.main" : "text.primary",
    }}>
        {(t.daysOverdue ?? 0) > 0 ? `+${t.daysOverdue}` : t.daysOverdue}
    </Box>;

// Umbrales recalibrados con la semántica nueva (#12844): antes `daysOverdue` contaba desde la
// EMISIÓN, y 30/60 eran «lleva un mes / dos meses sin pagar». Ahora cuenta desde el VENCIMIENTO,
// así que un 30 aquí ya es un mes de retraso real y no puede seguir pintándose de naranja.
const BillDays = ({a}: { a: BillAlertDto }) => (
    <Box component="span" sx={{
        fontWeight: 700, fontVariantNumeric: "tabular-nums",
        color: (a.daysOverdue ?? 0) > 30 ? "error.main" : (a.daysOverdue ?? 0) > 0 ? "warning.main" : "text.primary",
    }}>
        {a.daysOverdue}
    </Box>
);

const billTypeChip = (a: BillAlertDto) =>
    <SoftChip label={a.type === "SUPPLIER" ? "Proveedor" : "Cliente"} tone={a.type === "SUPPLIER" ? "warning" : "info"}/>;

export const BillAlertsPage = () => {
    const apiManager = useApiManager();
    const navigate = useNavigate();
    const theme = useTheme();
    const compact = useMediaQuery(theme.breakpoints.down("md"));
    const [alerts, setAlerts] = useState<BillAlertDto[]>([]);
    const [taxAlerts, setTaxAlerts] = useState<TaxAlertDto[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const billCtrl = apiManager.factory(BillAlertRestControllerApiFactory);
        const taxCtrl = apiManager.factory(TaxCalendarRestControllerApiFactory);
        Promise.all([
            apiManager.execute(billCtrl.getAlerts()).then(list => setAlerts(list as BillAlertDto[])),
            apiManager.execute(taxCtrl.getTaxAlerts()).then(list => setTaxAlerts(list as TaxAlertDto[])),
        ]).finally(() => setLoading(false));
    }, []);

    const handleClick = (alert: BillAlertDto) => {
        if (alert.type === "SUPPLIER") {
            navigate(`/app/admin/accounting/supplier-bill/${alert.id}`);
        } else {
            navigate(`/app/admin/billing/bill/${alert.id}`);
        }
    };

    // Pinchar una obligación es ir a marcarla como hecha: su presentación si ya hay una registrada,
    // o el alta de una nueva con modelo, ejercicio y periodo puestos. El correo diario enlaza igual.
    const handleTaxClick = (t: TaxAlertDto) => {
        if (t.submissionId) {
            navigate(`/app/admin/accounting/tax-submission/${t.submissionId}`);
        } else {
            const q = new URLSearchParams({
                modelType: t.modelType ?? "",
                year: String(t.coveredYear),
                period: t.period ?? "",
            });
            navigate(`/app/admin/accounting/tax-submission/new?${q}`);
        }
    };

    if (loading) return (
        <Page>
            <PageHeader title="Vencimientos"/>
            <SkeletonRows rows={6}/>
        </Page>
    );

    return (
        <Page>
            <PageHeader title="Vencimientos" subtitle="Obligaciones fiscales y facturas vencidas o a punto de vencer."/>

            <Stack spacing={3}>
                {/* Lo fiscal va PRIMERO y en la MISMA pantalla que las facturas, no en un panel aparte:
                    un segundo listado de avisos compite con el primero y acaban perdiendo los dos. Y va
                    arriba porque su fecha es un plazo legal, no una expectativa de cobro. */}
                <Panel title="Obligaciones fiscales">
                    {taxAlerts.length === 0 ? (
                        <EmptyState icon={<EventAvailable/>} title="Nada vencido ni a punto de vencer."/>
                    ) : compact ? (
                        <Stack spacing={1.5}>
                            {taxAlerts.map((t) => (
                                <ListCard key={`${t.code}-${t.coveredYear}-${t.period}`} onClick={() => handleTaxClick(t)}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                                        <Typography variant="body2" fontWeight={700}>{t.code} · {taxPeriod(t)}</Typography>
                                        {taxStatusChip(t.status)}
                                    </Stack>
                                    <Typography variant="body2" sx={{mt: 0.5}}>{t.label}</Typography>
                                    {t.status === "PRESENTADA" && t.reference && (
                                        <Typography variant="caption" color="text.secondary" display="block">{t.reference}</Typography>
                                    )}
                                    <Stack direction="row" spacing={2} alignItems="center" sx={{mt: 1}}>
                                        <Typography variant="caption" color="text.secondary">Vence {dateToString(t.dueDate)}</Typography>
                                        {taxFiler(t)}
                                        <Box sx={{flex: 1}}/>
                                        <TaxDays t={t}/>
                                    </Stack>
                                </ListCard>
                            ))}
                        </Stack>
                    ) : (
                        <TableContainer sx={tableContainerSx}>
                            <Table size="small" sx={{...tableSx, tableLayout: "fixed", minWidth: 940}}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{width: 120}}>Estado</TableCell>
                                        <TableCell sx={{width: 90}}>Modelo</TableCell>
                                        <TableCell>Concepto</TableCell>
                                        <TableCell sx={{width: 120}}>Periodo</TableCell>
                                        <TableCell sx={{width: 120}}>Vence</TableCell>
                                        <TableCell sx={{width: 110}}>Presenta</TableCell>
                                        <TableCell align="right" sx={{width: 90}}>Días</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {taxAlerts.map((t) => (
                                        <TableRow
                                            key={`${t.code}-${t.coveredYear}-${t.period}`}
                                            hover
                                            sx={{cursor: "pointer"}}
                                            onClick={() => handleTaxClick(t)}
                                        >
                                            <TableCell>{taxStatusChip(t.status)}</TableCell>
                                            <TableCell><Typography variant="body2" fontWeight={600}>{t.code}</Typography></TableCell>
                                            <TableCell>
                                                {t.label}
                                                {/* El NRC va junto al concepto y no en la columna de días: es
                                                    la prueba de que se presentó, no una cantidad de días. */}
                                                {t.status === "PRESENTADA" && t.reference && (
                                                    <Typography variant="caption" color="text.secondary" display="block">
                                                        {t.reference}
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>{taxPeriod(t)}</TableCell>
                                            <TableCell sx={{fontVariantNumeric: "tabular-nums"}}>{dateToString(t.dueDate)}</TableCell>
                                            <TableCell>{taxFiler(t)}</TableCell>
                                            <TableCell align="right"><TaxDays t={t}/></TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Panel>

                <Panel title="Facturas" subtitle="Días contados desde el vencimiento.">
                    {alerts.length === 0 ? (
                        <EmptyState icon={<ReceiptLong/>} title="No hay facturas vencidas."/>
                    ) : compact ? (
                        <Stack spacing={1.5}>
                            {alerts.map((a) => (
                                <ListCard key={`${a.type}-${a.id}`} onClick={() => handleClick(a)}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                                        <Typography variant="body2" fontWeight={700} noWrap>{a.counterpartyName}</Typography>
                                        {billTypeChip(a)}
                                    </Stack>
                                    <Stack direction="row" spacing={2} alignItems="center" sx={{mt: 1}}>
                                        <Typography variant="caption" color="text.secondary">
                                            {a.billNumber ?? "—"} · vence {dateToString(a.referenceDate)}
                                        </Typography>
                                        <Box sx={{flex: 1}}/>
                                        <Typography variant="body2" fontWeight={600} sx={{fontVariantNumeric: "tabular-nums"}}>{fmtEur(a.amount)}</Typography>
                                        <BillDays a={a}/>
                                    </Stack>
                                </ListCard>
                            ))}
                        </Stack>
                    ) : (
                        <TableContainer sx={tableContainerSx}>
                            <Table size="small" sx={{...tableSx, tableLayout: "fixed", minWidth: 820}}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{width: 120}}>Tipo</TableCell>
                                        <TableCell sx={{width: 130}}>Nº factura</TableCell>
                                        <TableCell>Contraparte</TableCell>
                                        <TableCell align="right" sx={{width: 130}}>Importe</TableCell>
                                        {/* Desde #12844 `referenceDate` es el VENCIMIENTO en las dos filas
                                            —proveedor y cliente—, no la emisión. La columna se llamaba «Fecha
                                            referencia» y era ambigua justo en lo que importa. */}
                                        <TableCell sx={{width: 120}}>Vence</TableCell>
                                        <TableCell align="right" sx={{width: 120}}>Días vencida</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {alerts.map((a) => (
                                        <TableRow
                                            key={`${a.type}-${a.id}`}
                                            hover
                                            sx={{cursor: "pointer"}}
                                            onClick={() => handleClick(a)}
                                        >
                                            <TableCell>{billTypeChip(a)}</TableCell>
                                            <TableCell>{a.billNumber ?? "—"}</TableCell>
                                            <TableCell><Typography variant="body2" fontWeight={600} noWrap>{a.counterpartyName}</Typography></TableCell>
                                            <TableCell align="right" sx={{fontVariantNumeric: "tabular-nums"}}>{fmtEur(a.amount)}</TableCell>
                                            <TableCell sx={{fontVariantNumeric: "tabular-nums"}}>{dateToString(a.referenceDate)}</TableCell>
                                            <TableCell align="right"><BillDays a={a}/></TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Panel>
            </Stack>
        </Page>
    );
};
